import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { EditAssetProp } from '@web/components/attachment/AssetEditorModal';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { AssetType } from '@packages/domain/techline/assets';
import { AssetMediaType } from '@packages/models/api';
import { Typography } from '@packages/ui/shared';
import AssetEditorModal from '@web/components/attachment/AssetEditorModal';
import { AssetViewerModal, type IAsset } from '@web/components/attachment/AssetViewerModal';
import { analyticsService } from '@web/services/singletons';
import { downloadFile } from '@web/utils';
import { useCallback, useMemo, useState } from 'react';

import { AttachmentList } from '../../../../attachment-list/attachment-list';
import { CollapsibleSection } from '../collapsible-section';
import { ConfirmDeleteAssetModal } from './confirm-delete-asset-modal';
import { DtcCodes } from './dtc-codes';

const SECTION_TITLE = 'Tech Description & Attachments';
const SECTION_TITLE_ATTACHMENTS_ONLY = 'Attachments';

/**
 * Props for the {@link TechDescriptionSection} component.
 */
export type TechDescriptionSectionProps = {
    /** The list of case-level asset attachments. */
    assetData: TechlineAssetEntity[];

    /** The Techline case details; used to display the technician description field. */
    caseDetails: TechlineCaseEntity | undefined;

    /** Whether the user can edit the submitted case. Users with the DSQM role cannot edit submitted cases. */
    canEditSubmittedCase: boolean;

    /** The list of DTC codes associated with the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Whether the case is in a closed status. Disables upload, edit, and delete on attachments. */
    isCaseClosed?: boolean;

    /** Delete a case-level asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Report an error message to the parent for display. */
    onError: (message: string) => void;

    /** Refresh the asset list from the API after mutations. */
    onRefreshAssets: () => Promise<void>;

    /** Report a success message to the parent for display. */
    onSuccess: (message: string) => void;

    /** Update a case-level asset's metadata or file content. */
    onUpdateAsset: (
        assetId: string,
        update: {
            /** Replacement file blob. */
            blob?: Blob;

            /** Updated asset name. */
            name?: string;

            /** Updated asset note. */
            note?: string;
        }
    ) => Promise<void>;

    /** Upload a new file as a case-level asset. */
    onUploadAsset: (file: File) => Promise<void>;

    /** Whether the tech description section should be displayed. */
    allowTechDescription: boolean;

    /** Whether DTC codes can be added to this case. */
    allowDtcCodes: boolean;
};

/**
 * Local state shape for the asset viewer modal.
 */
type ViewerState = {
    /** The mapped assets to display in the viewer. */
    attachments: IAsset[];

    /** The index of the initially selected asset. */
    initialIndex: number;

    /** Whether the viewer modal is currently visible. */
    visible: boolean;
};

/** Inline style for the attachment caption left margin. */
const attachmentCaptionStyle = { marginLeft: 4 };

// Helper functions

/**
 * Encapsulates the "Tech Description & Attachments" collapsible section of the submitted case view.
 * Renders the technician description, DTC codes, attachment list, and the viewer/editor/delete-confirmation
 * modals for managing case-level assets.
 *
 * @param props - {@link TechDescriptionSectionProps}
 * @returns the tech description section JSX
 */
export function TechDescriptionSection({
    allowDtcCodes,
    allowTechDescription,
    assetData,
    canEditSubmittedCase,
    caseDetails,
    dtcCodeData,
    isCaseClosed = false,
    onDeleteAsset,
    onError,
    onRefreshAssets,
    onSuccess,
    onUpdateAsset,
    onUploadAsset,
}: TechDescriptionSectionProps) {
    // State
    const [assetIndex, setAssetIndex] = useState(0);
    const [attachmentsSectionExpanded, setAttachmentsSectionExpanded] = useState(true);
    const [isAssetModalVisible, setIsAssetModalVisible] = useState(false);
    const [isDeletingAsset, setIsDeletingAsset] = useState(false);
    const [pendingDeleteAssetId, setPendingDeleteAssetId] = useState<string | null>(null);
    const [viewerState, setViewerState] = useState<ViewerState>({
        attachments: [],
        initialIndex: 0,
        visible: false,
    });

    const sectionTitle = allowTechDescription ? SECTION_TITLE : SECTION_TITLE_ATTACHMENTS_ONLY;

    // Derived state

    /**
     * Maps complete assets to the shape expected by the asset editor modal.
     * Only includes images with a "complete" upload status that are not a worksheet asset.
     */
    const editorAssets = useMemo(
        () =>
            assetData
                .filter(
                    (asset) =>
                        asset.status === 'complete' && asset.assetType === AssetType.Image && !asset.isWorksheetAsset
                )
                .map((asset) => mapAssetToEditAssetProp(asset)),
        [assetData]
    );

    // Handlers

    /**
     * Handle cancelling the pending asset deletion. Clears the pending delete state
     * so the confirmation modal is dismissed.
     */
    const handleCancelDeleteAsset = useCallback(() => {
        setPendingDeleteAssetId(null);
    }, []);

    /**
     * Handle confirming the pending asset deletion. Calls the parent's delete callback,
     * refreshes the asset list, and reports success or error.
     */
    const handleConfirmDeleteAsset = useCallback(async () => {
        if (!pendingDeleteAssetId) {
            return;
        }

        try {
            setIsDeletingAsset(true);
            await onDeleteAsset(pendingDeleteAssetId);
            onSuccess('Asset deleted successfully!');
        } catch {
            onError('Failed to delete asset. Please try again.');
        } finally {
            setIsDeletingAsset(false);
            setPendingDeleteAssetId(null);
        }
    }, [onDeleteAsset, onError, onSuccess, pendingDeleteAssetId]);

    /**
     * Handle the delete button click on an attachment. Sets the pending delete asset ID
     * to trigger the confirmation modal instead of deleting immediately.
     *
     * @param assetId - the ID of the asset the user wants to delete
     */
    const handleDeleteAssetButtonClick = useCallback((assetId: string) => {
        setPendingDeleteAssetId(assetId);
    }, []);

    /**
     * Handle downloading a case-level asset. Looks up the asset's original media URL
     * by ID and triggers a browser download.
     *
     * @param assetId - the ID of the asset to download
     */
    const handleDownloadAssetButtonClick = useCallback(
        async (assetId: string) => {
            const asset = assetData.find((a) => a.id === assetId);
            if (!asset?.downloadUrl) {
                return;
            }

            await downloadFile(asset.downloadUrl, asset.name || 'attachment');
        },
        [assetData]
    );

    /**
     * Opens the asset editor modal at the index matching the given asset ID.
     *
     * @param assetId - the ID of the asset to edit
     */
    const handleEditAssetButtonClick = useCallback(
        (assetId: string) => {
            const index = editorAssets.findIndex((a) => a.assetId === assetId);
            setAssetIndex(Math.max(index, 0));
            setIsAssetModalVisible(true);
        },
        [editorAssets]
    );

    /** Closes the asset editor modal. */
    const handleEditorClose = useCallback(() => {
        setIsAssetModalVisible(false);
    }, []);

    /**
     * Handles deleting an asset from within the editor modal. Shows the delete confirmation
     * modal and refreshes the asset list afterward.
     *
     * @param id - the ID of the asset to delete
     */
    const handleEditorDeleteAsset = useCallback(
        async (id: string) => {
            handleDeleteAssetButtonClick(id);
            await onRefreshAssets();
        },
        [handleDeleteAssetButtonClick, onRefreshAssets]
    );

    /**
     * Downloads an asset by URL and filename (used by both the viewer and editor modals).
     *
     * @param imageSrc - the URL of the image to download
     * @param fileName - the filename for the downloaded file
     */
    const handleAssetDownload = useCallback(async (imageSrc: string, fileName: string) => {
        await downloadFile(imageSrc, fileName);
    }, []);

    /**
     * Handle updating a case-level asset from the editor modal. Calls the parent's update
     * callback, refreshes assets, and reports success or error.
     *
     * @param assetId - the ID of the asset to update
     * @param update - the update payload (blob, name, note)
     */
    const handleUpdateAssetConfirm = useCallback(
        async (
            assetId: string,
            update: {
                /** Replacement file blob. */
                blob?: Blob;
                /** Updated asset name. */
                name?: string;
                /** Updated asset note. */
                note?: string;
            }
        ) => {
            try {
                await onUpdateAsset(assetId, update);
                await onRefreshAssets();
                onSuccess('Asset updated successfully!');
            } catch {
                onError('Failed to update asset. Please try again.');
            }
        },
        [onError, onRefreshAssets, onSuccess, onUpdateAsset]
    );

    /**
     * Handle uploading new case-level assets. Logs analytics events, expands the
     * attachments section, and uploads each file sequentially.
     *
     * @param files - the files to upload
     */
    const handleUploadAttachment = useCallback(
        (files: File[]) => {
            // Log the start of the attachment flow
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_ATTACHMENT_FLOW);
            setAttachmentsSectionExpanded(true);

            // Upload each file, reporting errors individually
            for (const file of files) {
                onUploadAsset(file).catch(() => {
                    onError('Media uploading failed. Please try again!');
                });
            }
        },
        [onError, onUploadAsset]
    );

    /**
     * Opens the asset viewer modal for the given asset, mapping all complete assets
     * to the viewer shape and finding the correct initial index.
     *
     * @param assetId - the ID of the asset to view
     */
    const handleViewAssetButtonClick = useCallback(
        (assetId: string) => {
            // Build the viewer asset list from complete assets
            const completeAssets = assetData.filter((a) => a.status === 'complete');
            const viewerAssets = completeAssets.map((asset) => mapAssetToViewerAsset(asset));
            const index = viewerAssets.findIndex((a) => a.id === assetId);

            setViewerState({ attachments: viewerAssets, initialIndex: Math.max(index, 0), visible: true });
        },
        [assetData]
    );

    /** Closes the asset viewer modal and resets viewer state. */
    const handleViewerClose = useCallback(() => {
        setViewerState({ attachments: [], initialIndex: 0, visible: false });
    }, []);

    return (
        <CollapsibleSection
            expanded={attachmentsSectionExpanded}
            onExpandedChange={setAttachmentsSectionExpanded}
            showDividerTop
            title={sectionTitle}
        >
            {/* Technician Description */}
            {allowTechDescription && (
                <div className="mb-4">
                    <Typography color="black" variant="label">
                        Technician&apos;s description of customer&apos;s concern
                    </Typography>
                    <div className="mt-1">
                        <Typography color="black">{caseDetails?.technicianDescription}</Typography>
                    </div>
                </div>
            )}

            {/* DTC Codes */}
            {allowDtcCodes && (
                <div className="mb-4 gap-4">
                    <Typography color="black" variant="label">
                        DTC codes
                    </Typography>
                    {dtcCodeData?.length > 0 ? (
                        <DtcCodes dtcCodeData={dtcCodeData} />
                    ) : (
                        <div className="mt-1">
                            <Typography color="black">--</Typography>
                        </div>
                    )}
                </div>
            )}

            {/* Attachments */}
            <div>
                {allowTechDescription && (
                    <div className="mb-1 mt-4">
                        <Typography color="black" variant="label">
                            Attachments
                        </Typography>
                        <Typography color="textDarkSecondary" style={attachmentCaptionStyle} variant="caption">
                            Upload extra files if requested or needed for diagnosis.
                        </Typography>
                    </div>
                )}
                <AttachmentList
                    assetData={assetData}
                    isReadOnly={isCaseClosed || !canEditSubmittedCase}
                    onDelete={handleDeleteAssetButtonClick}
                    onDownload={handleDownloadAssetButtonClick}
                    onEdit={handleEditAssetButtonClick}
                    onUpload={handleUploadAttachment}
                    onView={handleViewAssetButtonClick}
                />
                <div />
            </div>

            {/* Asset Viewer Modal */}
            {viewerState.visible && (
                <AssetViewerModal
                    assets={viewerState.attachments}
                    initialIndex={viewerState.initialIndex}
                    onClose={handleViewerClose}
                    onDownload={handleAssetDownload}
                />
            )}
            {/* Asset Editor Modal */}
            {isAssetModalVisible && (
                <AssetEditorModal
                    assets={editorAssets}
                    edit={isAssetModalVisible}
                    initialIndex={assetIndex}
                    onClose={handleEditorClose}
                    onDeleteAsset={handleEditorDeleteAsset}
                    onDownload={handleAssetDownload}
                    onUpdateAsset={handleUpdateAssetConfirm}
                />
            )}
            {/* Delete Confirmation Modal */}
            <ConfirmDeleteAssetModal
                isLoading={isDeletingAsset}
                onCancel={handleCancelDeleteAsset}
                onConfirm={handleConfirmDeleteAsset}
                show={Boolean(pendingDeleteAssetId)}
            />
        </CollapsibleSection>
    );
}

/**
 * Finds a media entry on an asset by its image type ID.
 *
 * @param asset - the techline asset entity to search
 * @param typeId - the asset media type to find (e.g. Original, Thumbnail)
 * @returns the matching media entry, or undefined
 */
function findMediaByType(
    asset: TechlineAssetEntity,
    typeId: AssetMediaType
): TechlineAssetEntity['media'][number] | undefined {
    return asset.media?.find((m) => m.assetImageTypeId === typeId);
}

/**
 * Maps a `TechlineAssetEntity` to the `EditAssetProp` shape expected by `AssetEditorModal`.
 *
 * @param asset - the asset to map
 * @returns the mapped edit asset prop
 */
function mapAssetToEditAssetProp(asset: TechlineAssetEntity): EditAssetProp {
    const thumbnail = findMediaByType(asset, AssetMediaType.Thumbnail);

    // Map media entries to the shape expected by AssetEditorModal
    const media = (asset.media || [])
        // eslint-disable-next-line jsdoc/require-jsdoc
        .filter((m): m is typeof m & { assetImageTypeId: string } => Boolean(m.assetImageTypeId))
        .map((m) => ({ assetImageTypeId: m.assetImageTypeId, url: m.url }));

    return {
        assetId: asset.id,
        assetTypeId: asset.assetType,
        downloadUrl: asset.downloadUrl,
        media,
        name: asset.name,
        note: asset.note || '',
        preview: thumbnail?.url || asset.downloadUrl,
    };
}

/**
 * Maps a `TechlineAssetEntity` to the `IAsset` shape expected by `AssetViewerModal`.
 *
 * @param asset - the asset to map
 * @returns the mapped viewer asset
 */
function mapAssetToViewerAsset(asset: TechlineAssetEntity): IAsset {
    return {
        assetType: asset.assetType,
        downloadUrl: asset.downloadUrl,
        id: asset.id,
        name: asset.name,
        thumbnailUrl: asset?.thumbnailUrl,
        uploadedAt: new Date(asset.createdAt),
        contentType: asset.contentType,
    };
}
